/**
 * Vite-served environment variables.
 *
 * `VITE_API_URL` is the single runtime knob that points the FE at the
 * API. Two distinct values:
 *
 *   - Local dev (running Vite outside docker): `http://localhost:3000`
 *     — Vite hits the API directly on the host loopback.
 *   - Inside docker (or via Caddy wildcard): the request goes to
 *     `${origin}/api/*` and Caddy proxies to api:3000. We derive this
 *     at runtime from `window.location.origin` so the same bundle works
 *     in both modes; the env var only acts as an explicit OVERRIDE for
 *     prod or for a remote-tunneling setup.
 *   - Empty string: treat as "use same origin" — useful when Vite's
 *     own dev proxy is configured to forward `/api` to the API
 *     container (current Slice 0 docker setup).
 */
const EXPLICIT_API_URL = import.meta.env.VITE_API_URL?.trim() ?? '';

export const API_BASE = (() => {
  if (EXPLICIT_API_URL) {
    return EXPLICIT_API_URL.replace(/\/$/, '');
  }
  // SSR-safe: only touch `window` when it actually exists. The
  // optional-chained `window?.location?.origin` still parses the
  // identifier in strict mode, which throws a ReferenceError in the
  // Node test environment (no jsdom). Wrap with the explicit
  // `typeof` guard so `renderToStaticMarkup` smoke tests keep
  // working in node-env vitest projects.
  // biome-ignore lint/complexity/useOptionalChain: explicit typeof guard is required in node-env tests
  if (typeof window !== 'undefined' && window.location?.origin) {
    return `${window.location.origin}/api`;
  }
  // Fallback for SSR / tests: hit the api container via the docker DNS name.
  return 'http://api:3000/api';
})();

/**
 * Resolve the current tenant slug from the host header.
 *
 * Mirrors the proxy/Caddy tenant-resolution logic so the FE shows
 * the same slug the API will use for the next request.
 *
 * Multi-tenant via subdomain is OPTIONAL. Bare hosts and reserved
 * prefixes (`www`) resolve to the `default` tenant so the badge is
 * accurate even when no subdomain is used.
 *
 * For `tenant1.erp.globalesmedical.localhost` → "tenant1".
 * For `tenant1.erp2.globalesmedical.com`       → "tenant1".
 * For `localhost`, `erp2.globalesmedical.com`, or `www.…` → "default".
 */
const DEFAULT_TENANT_SLUG = 'default';
const PUBLIC_SUFFIXES = ['erp.globalesmedical.localhost', 'erp2.globalesmedical.com'] as const;
const RESERVED_LABELS = new Set(['www']);
function resolveTenantSlugFromHost(host: string): string {
  for (const suffix of PUBLIC_SUFFIXES) {
    if (host === suffix) return DEFAULT_TENANT_SLUG;
    if (host.endsWith(`.${suffix}`)) {
      const firstLabel = host.slice(0, host.length - suffix.length - 1);
      if (firstLabel && !RESERVED_LABELS.has(firstLabel)) return firstLabel;
      return DEFAULT_TENANT_SLUG;
    }
  }
  return DEFAULT_TENANT_SLUG;
}
export function currentTenantSlug(): string {
  if (typeof window === 'undefined') return DEFAULT_TENANT_SLUG;
  return resolveTenantSlugFromHost(window.location.hostname);
}
